import React, { useRef, useEffect, useState, useCallback } from "react";
import styles from "./ShopPositioner2D.module.css";

interface ShopPositioner2DProps {
  floorMapUrl: string;
  shopIconUrl?: string;
  initialPositionX?: number;
  initialPositionY?: number;
  initialPositionZ?: number;
  initialScaleX?: number;
  initialScaleY?: number;
  onPositionChange: (position: {
    x: number;
    y: number;
    z: number;
    scaleX: number;
    scaleY: number;
  }) => void;
}

export default function ShopPositioner2D({
  floorMapUrl,
  shopIconUrl,
  initialPositionX = 0,
  initialPositionY = 0,
  initialPositionZ = 0,
  initialScaleX = 1,
  initialScaleY = 1,
  onPositionChange,
}: ShopPositioner2DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  const [position, setPosition] = useState({
    x: Number(initialPositionX) || 0,
    y: Number(initialPositionY) || 0,
    z: Number(initialPositionZ) || 0,
  });
  const [isPanning, setIsPanning] = useState(false);
  const [isDraggingShop, setIsDraggingShop] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [shopIcon, setShopIcon] = useState<HTMLImageElement | null>(null);
  const [floorMap, setFloorMap] = useState<HTMLImageElement | null>(null);

  // Update pan ref when pan changes
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  // Update position when initial positions change
  useEffect(() => {
    setPosition({
      x: Number(initialPositionX) || 0,
      y: Number(initialPositionY) || 0,
      z: Number(initialPositionZ) || 0,
    });
  }, [initialPositionX, initialPositionY, initialPositionZ]);

  // Load shop icon
  useEffect(() => {
    if (shopIconUrl) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setShopIcon(img);
      img.onerror = () => {
        console.error("Failed to load shop icon");
        setShopIcon(null);
      };
      img.src = shopIconUrl;
    } else {
      // Очищаем иконку, если URL не передан
      setShopIcon(null);
    }
  }, [shopIconUrl]);

  // Load floor map
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setFloorMap(img);
    img.onerror = () => console.error("Failed to load floor map");
    img.src = floorMapUrl;
  }, [floorMapUrl]);

  // Draw function
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate scale to fit if map exists
    let scaleToFit = 1;
    if (floorMap) {
      scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9 // Leave 10% margin on each side
      );
    }

    // Draw floor map with zoom and pan (matching TerminalPositioner2D approach)
    if (floorMap) {
      // Save context
      ctx.save();

      // Calculate scaled dimensions with scale applied
      const scaledWidth = floorMap.width * scaleToFit * scale;
      const scaledHeight = floorMap.height * scaleToFit * scale;

      // Center position with pan offset
      const mapX = (canvas.width - scaledWidth) / 2 + pan.x;
      const mapY = (canvas.height - scaledHeight) / 2 + pan.y;

      // Draw map with scaling and pan
      ctx.drawImage(floorMap, mapX, mapY, scaledWidth, scaledHeight);

      // Restore context
      ctx.restore();
    }

    // Draw shop icon with same coordinate system as map
    ctx.save();

    // Apply transformations to match map position and scale
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.translate(pan.x, pan.y);
    ctx.scale(scale * scaleToFit, scale * scaleToFit);

    // Calculate shop position
    const posX = Number(position.x) || 0;
    const posY = Number(position.y) || 0;

    // Shop position in scaled coordinates
    const shopCanvasX = posX * 100;
    const shopCanvasY = posY * 100;

    // Draw shop icon if available
    if (shopIcon) {
      const scaleX = Number(initialScaleX) || 1;
      const scaleY = Number(initialScaleY) || 1;
      const iconWidth = shopIcon.width * scaleX;
      const iconHeight = shopIcon.height * scaleY;
      ctx.drawImage(
        shopIcon,
        shopCanvasX - iconWidth / 2,
        shopCanvasY - iconHeight / 2,
        iconWidth,
        iconHeight
      );
    } else {
      // Draw a placeholder marker
      ctx.fillStyle = "rgba(102, 126, 234, 0.8)";
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(shopCanvasX, shopCanvasY, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    // Restore context
    ctx.restore();
  }, [floorMap, shopIcon, scale, pan, position, initialScaleX, initialScaleY]);

  // Update canvas size and redraw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      draw();
    };

    resize();
    
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [draw]);

  // Redraw when shopIcon changes (after draw is defined)
  useEffect(() => {
    draw();
  }, [shopIcon, draw]);

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate map scale to fit
    const mapScale = Math.min(
      canvas.width / floorMap.width,
      canvas.height / floorMap.height,
      0.9
    );
    const totalScale = scale * mapScale;

    // Calculate shop position in canvas coordinates
    const posX = Number(position.x) || 0;
    const posY = Number(position.y) || 0;
    const shopCanvasX = canvas.width / 2 + posX * 100 * totalScale + pan.x;
    const shopCanvasY = canvas.height / 2 + posY * 100 * totalScale + pan.y;

    // Calculate distance from mouse to shop icon center
    const scaleX = Number(initialScaleX) || 1;
    const scaleY = Number(initialScaleY) || 1;
    let iconRadius = 30 * totalScale; // Default marker radius scaled
    if (shopIcon) {
      const iconSize =
        Math.max(shopIcon.width * scaleX, shopIcon.height * scaleY) * totalScale;
      iconRadius = iconSize / 2;
    }

    const dx = mouseX - shopCanvasX;
    const dy = mouseY - shopCanvasY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If clicking on shop icon, start dragging it
    if (distance < iconRadius + 20) {
      setIsDraggingShop(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
      e.preventDefault();
      return;
    }

    // Otherwise, start panning
    setIsPanning(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    if (isDraggingShop) {
      // Calculate new position for shop icon
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate map scale to fit
      const mapScale = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9
      );
      const totalScale = scale * mapScale;

      // Convert mouse position to shop coordinates
      const x = (mouseX - canvas.width / 2 - pan.x) / totalScale;
      const y = (mouseY - canvas.height / 2 - pan.y) / totalScale;

      const newX = x / 100;
      const newY = y / 100;
      const newZ = Number(initialPositionZ) || 0;

      setPosition({
        x: newX,
        y: newY,
        z: newZ,
      });

      onPositionChange({
        x: newX,
        y: newY,
        z: newZ,
        scaleX: Number(initialScaleX) || 1,
        scaleY: Number(initialScaleY) || 1,
      });

      setLastMousePos({ x: e.clientX, y: e.clientY });
    } else if (isPanning) {
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;
      setPan((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsPanning(false);
    setIsDraggingShop(false);
  };

  // Handle scale slider change
  const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScale(parseFloat(e.target.value));
  };

  // Handle double click to place shop
  const handleDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const rect = canvas.getBoundingClientRect();
    // Get mouse position relative to canvas
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate map scale to fit
    const mapScale = Math.min(
      canvas.width / floorMap.width,
      canvas.height / floorMap.height,
      0.9
    );

    // Calculate position relative to center of canvas (accounting for pan and scale)
    // Center of canvas is the origin for shop coordinates
    const totalScale = scale * mapScale;
    const x = (mouseX - canvas.width / 2 - pan.x) / totalScale;
    const y = (mouseY - canvas.height / 2 - pan.y) / totalScale;

    // Convert to shop coordinates (divide by 100 as per original logic)
    const newX = x / 100;
    const newY = y / 100;
    const newZ = Number(initialPositionZ) || 0;

    setPosition({
      x: newX,
      y: newY,
      z: newZ,
    });

    onPositionChange({
      x: newX,
      y: newY,
      z: newZ,
      scaleX: Number(initialScaleX) || 1,
      scaleY: Number(initialScaleY) || 1,
    });
  };

  // Handle wheel for zooming with addEventListener to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const handleWheelCapture = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setScale((prev) => {
        const newScale = Math.max(0.5, Math.min(5, prev * delta));
        const scaleToFit = Math.min(
          canvas.width / floorMap.width,
          canvas.height / floorMap.height,
          0.9
        );
        const currentPan = panRef.current;
        const imageX = (mouseX - canvas.width / 2 - currentPan.x) / (scaleToFit * prev);
        const imageY = (mouseY - canvas.height / 2 - currentPan.y) / (scaleToFit * prev);
        setPan((prevPan) => ({
          x: prevPan.x - imageX * scaleToFit * (newScale - prev),
          y: prevPan.y - imageY * scaleToFit * (newScale - prev),
        }));
        return newScale;
      });
    };

    canvas.addEventListener("wheel", handleWheelCapture, {
      passive: false,
      capture: true,
    });

    return () => {
      canvas.removeEventListener("wheel", handleWheelCapture, {
        capture: true,
      });
    };
  }, [floorMap]);

  const handleReset = () => {
    const resetZ = Number(initialPositionZ) || 0;
    setPosition({ x: 0, y: 0, z: resetZ });
    setPan({ x: 0, y: 0 });
    setScale(1);
    onPositionChange({
      x: 0,
      y: 0,
      z: resetZ,
      scaleX: Number(initialScaleX) || 1,
      scaleY: Number(initialScaleY) || 1,
    });
  };

  return (
    <div className={styles.container}>
      <canvas
        className={styles.canvas}
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      />

      <div className={styles.instructions}>
        Перетаскивание иконки - позиционировать | Скролл - масштабирование |
        Перетаскивание карты - панорамирование | Двойной клик - установить позицию
      </div>
    </div>
  );
}
